import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { loadFilters } from '@/utils/filtersStorage';
import { SEOSegmentationFilterInput, SEOSeniorCareSubServices } from '@/__generated__/globalTypes';
import { MultiSelectPills } from '../../MultiSelectPills';

type ProfessionalSkillsType = Pick<
  SEOSegmentationFilterInput,
  'certifiedNursingAssistant' | 'seniorCareCprTrained' | 'homeHealthAid' | 'registeredNurse'
>;

interface ProfessionalSkillsProps {
  setProfessionalSkillsObject: Dispatch<SetStateAction<ProfessionalSkillsType>>;
  seniorCareSubServices: SEOSegmentationFilterInput['seniorCareSubServices'];
  setSeniorCareSubServices: Dispatch<
    SetStateAction<SEOSegmentationFilterInput['seniorCareSubServices']>
  >;
}
const ProfessionalSkills = ({
  setProfessionalSkillsObject,
  seniorCareSubServices,
  setSeniorCareSubServices,
}: ProfessionalSkillsProps) => {
  const localStorageFilters = loadFilters();
  const CERTIFIED_NURSING_ASSISTANT = 'CERTIFIED_NURSING_ASSISTANT';
  const SENIOR_CARE_CPR_TRAINED = 'SENIOR_CARE_CPR_TRAINED';
  const HOME_HEALTH_AIDE = 'HOME_HEALTH_AIDE';
  const REGISTERED_NURSE = 'REGISTERED_NURSE';
  const { DEMENTIA } = SEOSeniorCareSubServices;
  /*
   * The value of "Alzheimers/Dementia experience" currently does exist in our index.
   * Therefore, we are substituting its value for 'DEMENTIA' which exists in SEOSeniorCareSubServices
   */
  const seniorCareProfessionalSkillsList = [
    { label: 'Alzheimers/Dementia experience', value: DEMENTIA },
    { label: 'Certified nursing assistant', value: CERTIFIED_NURSING_ASSISTANT },
    { label: 'Cpr trained', value: SENIOR_CARE_CPR_TRAINED },
    { label: 'Home health aide', value: HOME_HEALTH_AIDE },
    { label: 'Registered nurse', value: REGISTERED_NURSE },
  ];

  const [professionalSkillsList, setProfessionalSkillsList] = useState<string[]>([]);

  // check if any professional skill filters are currently applied from local storage
  useEffect(() => {
    const currentProfessionalSkills: SEOSegmentationFilterInput = {
      certifiedNursingAssistant: false,
      seniorCareCprTrained: false,
      homeHealthAid: false,
      registeredNurse: false,
    };
    const currentProfessionalSkillsList: string[] = [];

    if (localStorageFilters?.certifiedNursingAssistant) {
      currentProfessionalSkills.certifiedNursingAssistant = true;
      currentProfessionalSkillsList.push(CERTIFIED_NURSING_ASSISTANT);
    }
    if (localStorageFilters?.seniorCareCprTrained) {
      currentProfessionalSkills.seniorCareCprTrained = true;
      currentProfessionalSkillsList.push(SENIOR_CARE_CPR_TRAINED);
    }
    if (localStorageFilters?.homeHealthAid) {
      currentProfessionalSkills.homeHealthAid = true;
      currentProfessionalSkillsList.push(HOME_HEALTH_AIDE);
    }
    if (localStorageFilters?.registeredNurse) {
      currentProfessionalSkills.registeredNurse = true;
      currentProfessionalSkillsList.push(REGISTERED_NURSE);
    }
    if (localStorageFilters?.seniorCareSubServices?.includes(DEMENTIA)) {
      currentProfessionalSkillsList.push(DEMENTIA);
    }

    setProfessionalSkillsObject(currentProfessionalSkills);
    setProfessionalSkillsList(currentProfessionalSkillsList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChangeEvent = (values: string[]) => {
    const professionalSkillsFilters: SEOSegmentationFilterInput = {
      certifiedNursingAssistant: false,
      seniorCareCprTrained: false,
      homeHealthAid: false,
      registeredNurse: false,
    };
    values.forEach((skill: string) => {
      switch (skill) {
        case CERTIFIED_NURSING_ASSISTANT:
          professionalSkillsFilters.certifiedNursingAssistant = true;
          break;
        case SENIOR_CARE_CPR_TRAINED:
          professionalSkillsFilters.seniorCareCprTrained = true;
          break;
        case HOME_HEALTH_AIDE:
          professionalSkillsFilters.homeHealthAid = true;
          break;
        case REGISTERED_NURSE:
          professionalSkillsFilters.registeredNurse = true;
          break;
        case DEMENTIA:
          if (!seniorCareSubServices || seniorCareSubServices.length < 1) {
            setSeniorCareSubServices([DEMENTIA]);
          } else if (seniorCareSubServices && !seniorCareSubServices.includes(DEMENTIA)) {
            const subServices = seniorCareSubServices.slice();
            subServices.push(DEMENTIA);
            setSeniorCareSubServices(subServices);
          }
          break;
        default:
        // do nothing
      }
    });

    // remove Dementia from subservices if it has not been selected
    if (!values.includes(DEMENTIA) && seniorCareSubServices?.includes(DEMENTIA)) {
      const subServices = seniorCareSubServices.slice();
      const index = seniorCareSubServices.indexOf(DEMENTIA);
      subServices.splice(index, 1);
      setSeniorCareSubServices(subServices);
    }

    setProfessionalSkillsList(values);
    setProfessionalSkillsObject(professionalSkillsFilters);
  };

  return (
    <MultiSelectPills
      sectionName="Professional skills"
      handleOnChangeEvent={handleOnChangeEvent}
      pillOptions={seniorCareProfessionalSkillsList}
      category="SENIOR_CARE_PROFESSIONAL_SKILLS"
      selectedValues={professionalSkillsList}
    />
  );
};

export { ProfessionalSkills };
